import React, { useState } from 'react';

import Access from 'ui/Access';
import PaperForm from 'ui/form/PaperForm';
import SectionForm from 'ui/form/SectionForm';
import Fields from 'ui/form/FieldsForm';
import ButtonsForm from 'ui/form/ButtonsForm';
import AccessForm from 'ui/form/AccessForm';
import * as valid from 'utils/validators';

import { withAppContext } from '../../../AppContext';

const CANCEL_ACTION = 'cancel.action';
const SAVE_ACTION = 'save.action';
const CLONE_ACTION = 'clone.action';

const validateRules = valid.rules([
    valid.notEmpty('name'),
    valid.notEmpty('description'),
]);

export default withAppContext(RoleForm);

function getActionsButtons({ mode, loading, locking, cloneable, onAction }) {
    if (mode === 'edit') {
        return (
            <React.Fragment>
                <ButtonsForm.BackButton
                    label="Cancel"
                    onClick={onAction(CANCEL_ACTION)}
                    loading={loading}
                />
                <ButtonsForm.NextButton
                    label="Save"
                    onClick={onAction(SAVE_ACTION)}
                    loading={loading}
                    disabled={locking}
                />
            </React.Fragment>
        );
    }
    if (mode === 'view') {
        if (cloneable) {
            return (
                <React.Fragment>
                    <ButtonsForm.BackButton
                        label="Cancel"
                        onClick={onAction(CANCEL_ACTION)}
                        loading={loading}
                    />
                    <ButtonsForm.NextButton
                        label="Clone as new role"
                        onClick={onAction(CLONE_ACTION)}
                        loading={loading}
                    />
                </React.Fragment>
            );
        }
        return (
            <ButtonsForm.NextButton
                label="OK"
                onClick={onAction(CANCEL_ACTION)}
                loading={loading}
            />
        );
    }
}

function RoleForm({
    app,
    loading,
    role,
    roles,
    mode,
    onSave = () => {},
    onCancel = () => {},
    onClone = () => {},
}) {
    const [locking, setLocking] = useState(countLocking(role && role.rules));
    const [errors, setErrors] = useState({});
    const [values, setValues] = useState(
        role || {
            name: '',
            description: '',
            rules: {},
        }
    );
    const editable = mode === 'edit';
    const onAction = actionName => async () => {
        switch (actionName) {
            case SAVE_ACTION:
                const errors = await validateRules(values);
                if (Object.keys(errors).length) {
                    setErrors(errors);
                    app.on.scroll.notify();
                } else {
                    onSave({
                        name: values.name,
                        description: values.description,
                        rules: filterEmptyRules(values.rules),
                    }).catch(errors => setErrors(errors));
                }
                break;
            case CANCEL_ACTION:
                onCancel();
                break;
            case CLONE_ACTION:
                onClone();
                break;
            default:
                break;
        }
    };

    const onChange = name => value => {
        if (name === 'rules') {
            const newRules = { ...values[name], ...value };
            setLocking(countLocking(newRules));
            setValues({ ...values, ...{ [name]: newRules } });
        } else {
            setValues({ ...values, ...{ [name]: value } });
        }
    };
    const buttons = getActionsButtons({
        mode,
        loading,
        locking,
        onAction,
        cloneable: app.canChange(Access.COMPANIES_PARTNER_ROLES),
    });
    return (
        <PaperForm>
            <BodyForm
                editable={editable}
                values={values}
                errors={errors}
                roles={roles}
                onChange={onChange}
                user={app.currentUser}
            />
            <ButtonsForm>{buttons}</ButtonsForm>
        </PaperForm>
    );
}

function BodyForm({ editable, values, errors, roles, onChange, user }) {
    return (
        <React.Fragment>
            <SectionForm title="Role details">
                <Fields.Text
                    label="Role name"
                    error={errors.name}
                    value={values.name}
                    disabled={!editable}
                    onChange={onChange('name')}
                />
                <Fields.Text
                    label="Role description"
                    error={errors.description}
                    value={values.description}
                    disabled={!editable}
                    onChange={onChange('description')}
                />
            </SectionForm>
            <SectionForm title="Access">
                <AccessForm>
                    {roles.map(role => (
                        <AccessForm.Group
                            disabled={!editable}
                            key={role.name}
                            title={role.label}
                            rules={values.rules}
                            options={role.options}
                            onChange={onChange('rules')}
                        />
                    ))}
                </AccessForm>
            </SectionForm>
        </React.Fragment>
    );
}

function countLocking(rules) {
    if (rules) {
        return !Object.values(rules).some(value => !!value);
    }
    return true;
}

function filterEmptyRules(rules) {
    return Object.keys(rules).reduce((acc, name) => {
        if (rules[name]) {
            acc[name] = rules[name];
        }
        return acc;
    }, {});
}
